import { useAuthStore } from '@/store/authStore';
import ConnectionTester from '@/components/debug/ConnectionTester';
import { ApiTestComponent } from '@/components/debug/ApiTestComponent';
import { ShieldAlert, Terminal, Activity, Cpu, Layers, ChevronLeft, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function TestingPage() {
  const { isAuthenticated, user } = useAuthStore();
  const navigate = useNavigate();

  // Only show in development or when explicitly enabled
  const isDevelopment = import.meta.env.DEV;
  const testingEnabled = import.meta.env.VITE_ENABLE_TESTING === 'true';

  if (!isDevelopment && !testingEnabled) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <div className="p-12 bg-white border border-gray-200 rounded-xl text-center max-w-lg shadow-sm">
          <ShieldAlert className="w-16 h-16 text-rose-600 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Access Denied
          </h1>
          <p className="text-gray-600 text-sm leading-relaxed mb-8">
            Diagnostic interface is restricted to development environments only.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-8 py-3 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-all"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // Require authentication for testing page
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <div className="p-12 bg-white border border-gray-200 rounded-xl text-center max-w-xl shadow-sm">
          <div className="w-16 h-16 bg-primary-50 border border-primary-200 rounded-lg flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-primary-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Authentication Required
          </h1>
          <p className="text-gray-600 text-sm leading-relaxed mb-8">
            Secure authentication is required to access the diagnostic tools. Please sign in to continue.
          </p>

          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-3 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-all"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="px-6 py-3 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-all"
            >
              Create Account
            </button>
          </div>

          <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3 text-left">
            <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 leading-relaxed">
              <strong>Notice:</strong> Testing tools require verified authentication to prevent unauthorized access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-24">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-6 lg:px-12 pt-16">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8 mb-16 border-b border-gray-200 pb-12">
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
            >
              <ChevronLeft className="w-6 h-6 text-gray-600" />
            </button>
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-1 bg-primary-600 rounded-full" />
                <span className="text-xs font-semibold text-primary-600 uppercase tracking-wider">Diagnostic Tools v4.0</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
                Diagnostic Dashboard
              </h1>
            </div>
          </div>

          <div className="px-6 py-4 bg-white border border-gray-200 rounded-lg flex items-center gap-6">
            <div className="text-right">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">User ID</div>
              <div className="text-xs font-mono text-primary-600 font-medium">
                {user?.id ? `${user.id.slice(0, 10).toUpperCase()}` : 'ANONYMOUS'}
              </div>
            </div>
            <div className="w-10 h-10 rounded-lg bg-primary-50 border border-primary-200 flex items-center justify-center">
              <Cpu className="w-5 h-5 text-primary-600" />
            </div>
          </div>
        </div>

        <div className="grid gap-12">
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4 ml-2">
              <Activity className="w-5 h-5 text-primary-600" />
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Connection Status</h2>
            </div>
            <ConnectionTester />
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4 ml-2">
              <Terminal className="w-5 h-5 text-primary-600" />
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">API Testing</h2>
            </div>
            <ApiTestComponent />
          </div>
        </div>
      </main>
    </div>
  );
}